import { useRef, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import {
  isRescheduleStructuredError,
  type ReschedulePreview,
  useMyPickupBooking,
  usePreviewReschedulePickupBooking,
  useReschedulePickupBooking,
} from "@/api/use-my-pickup-bookings";
import {
  formatDateInTz,
  formatSlotRangeInTz,
  todayPlusDaysIsoInTz,
} from "@/lib/dispatch-time";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string | null;
  initialShipmentId?: string;
}

function deliveryLabel(
  outcome: ReschedulePreview["deliveryOutcomes"][number],
  locale: string | undefined
): string {
  const format = (value: string) =>
    outcome.deliveredPrecision === "DATE"
      ? formatDateInTz(
          value.slice(0, 10),
          outcome.timeZone,
          "EEE, MMM d",
          locale
        )
      : new Intl.DateTimeFormat(locale, {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: outcome.timeZone,
        }).format(new Date(value));
  const fastest = format(outcome.deliveredFastest);
  const slowest = format(outcome.deliveredSlowest);
  return fastest === slowest ? fastest : `${fastest} – ${slowest}`;
}

export function PickupRescheduleDialog({
  open,
  onOpenChange,
  bookingId,
  initialShipmentId,
}: Props) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const bookingQuery = useMyPickupBooking(open ? bookingId : null);
  const booking = bookingQuery.data;
  const currentSlotId = booking?.slotId ?? booking?.slot?.id;
  const previewMutation = usePreviewReschedulePickupBooking();
  const moveMutation = useReschedulePickupBooking();
  const [initializedFor, setInitializedFor] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [newDate, setNewDate] = useState("");
  const [preview, setPreview] = useState<ReschedulePreview | null>(null);
  const [accepted, setAccepted] = useState<ReschedulePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submitLock = useRef(false);
  const pending = previewMutation.isPending || moveMutation.isPending;
  const eligible =
    booking?.shipments.filter((shipment) => shipment.canReschedulePickup) ?? [];
  const initializationKey = `${bookingId}:${initialShipmentId ?? "all"}`;
  if (open && booking && initializedFor !== initializationKey) {
    setInitializedFor(initializationKey);
    setSelectedIds(
      initialShipmentId
        ? eligible
            .filter((shipment) => shipment.id === initialShipmentId)
            .map((shipment) => shipment.id)
        : eligible.map((shipment) => shipment.id)
    );
    setNewDate("");
    setPreview(null);
    setAccepted(null);
    setError(null);
  }
  const close = () => {
    if (pending) return;
    setInitializedFor("");
    onOpenChange(false);
  };
  const invalidatePreview = () => {
    setPreview(null);
    setError(null);
  };
  const showError = (caught: unknown) => {
    if (
      isRescheduleStructuredError(caught) &&
      caught.response?.data.error === "DELIVERY_PLAN_INCOMPATIBLE" &&
      caught.response.data.details?.reason === "PICKUP_TIMING_UNAVAILABLE"
    ) {
      setError(t("pickup.detail.destinationTimingUnavailable"));
      return;
    }
    setError(
      isRescheduleStructuredError(caught)
        ? (caught.response?.data.message ?? t("pickup.detail.rescheduleFailed"))
        : t("pickup.detail.rescheduleFailed")
    );
  };
  const review = async () => {
    if (
      !booking ||
      !currentSlotId ||
      !newDate ||
      selectedIds.length === 0 ||
      submitLock.current
    )
      return;
    submitLock.current = true;
    setError(null);
    try {
      const result = await previewMutation.mutateAsync({
        bookingId: booking.id,
        shipmentIds: selectedIds,
        newScheduledDate: newDate,
        newSlotId: currentSlotId,
      });
      if (!result.proposedTiming) {
        setPreview(null);
        setError(t("pickup.detail.timingProposalUnavailable"));
        return;
      }
      if (
        !result.previewFingerprint ||
        result.deliveryOutcomes.length !== selectedIds.length ||
        result.deliveryOutcomes.some(
          (outcome) => !selectedIds.includes(outcome.shipmentId)
        )
      ) {
        throw new Error("Pickup preview has no complete delivery promise");
      }
      setPreview(result);
    } catch (caught) {
      setPreview(null);
      showError(caught);
    } finally {
      submitLock.current = false;
    }
  };
  const confirm = async () => {
    if (!booking || !currentSlotId || !preview || submitLock.current) return;
    submitLock.current = true;
    setError(null);
    try {
      const result = await moveMutation.mutateAsync({
        bookingId: booking.id,
        shipmentIds: selectedIds,
        newScheduledDate: newDate,
        newSlotId: currentSlotId,
        expectedPreviewFingerprint: preview.previewFingerprint,
        acceptCurrentLocationConfiguration: true,
      });
      setAccepted(result);
    } catch (caught) {
      if (isRescheduleStructuredError(caught)) {
        setPreview(null);
      }
      showError(caught);
    } finally {
      submitLock.current = false;
    }
  };
  const outcomes = accepted ?? preview;
  return (
    <Modal
      isOpen={open}
      onOpenChange={(next) => !next && close()}
      isDismissable={!pending}
      isKeyboardDismissDisabled={pending}
      size="lg"
      placement="center"
    >
      <ModalContent>
        <ModalHeader>
          {t(
            accepted
              ? "pickup.detail.rescheduledTitle"
              : "pickup.detail.rescheduleTitle"
          )}
        </ModalHeader>
        <ModalBody>
          {bookingQuery.isLoading && (
            <Spinner aria-label={t("pickup.detail.loadingMove")} />
          )}
          {bookingQuery.error && (
            <p role="alert">{t("pickup.detail.rescheduleFailed")}</p>
          )}
          {booking && !accepted && (
            <>
              <p className="text-sm text-default-600">
                {t("pickup.detail.moveSelectedHint", {
                  slot: booking.slot?.name ?? t("pickup.timingUnknown"),
                })}
              </p>
              <fieldset
                disabled={pending || booking.derivedState !== "Scheduled"}
                className="space-y-2"
              >
                <legend className="text-sm font-medium">
                  {t("pickup.detail.selectMoveShipments")}
                </legend>
                {eligible.map((shipment) => (
                  <label
                    key={shipment.id}
                    className="flex items-center gap-3 rounded-medium border border-divider p-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(shipment.id)}
                      onChange={(event) => {
                        setSelectedIds((current) =>
                          event.target.checked
                            ? [...current, shipment.id]
                            : current.filter((id) => id !== shipment.id)
                        );
                        invalidatePreview();
                      }}
                    />
                    <span>SHP-{shipment.shipmentNumber}</span>
                  </label>
                ))}
                {eligible.length === 0 && (
                  <p>{t("pickup.detail.noEligibleMoveShipments")}</p>
                )}
                <label className="block space-y-1 text-sm">
                  <span>{t("pickup.detail.newDate")}</span>
                  <input
                    type="date"
                    value={newDate}
                    min={
                      booking.dispatchCenter.timeZone
                        ? todayPlusDaysIsoInTz(
                            booking.dispatchCenter.timeZone,
                            1
                          )
                        : undefined
                    }
                    max={
                      booking.dispatchCenter.timeZone
                        ? todayPlusDaysIsoInTz(
                            booking.dispatchCenter.timeZone,
                            14
                          )
                        : undefined
                    }
                    onChange={(event) => {
                      setNewDate(event.target.value);
                      invalidatePreview();
                    }}
                    className="block w-full rounded-medium border border-divider px-3 py-2"
                  />
                </label>
              </fieldset>
              {selectedIds.length < eligible.length && (
                <p className="text-xs text-default-600">
                  {t("pickup.detail.unselectedStay")}
                </p>
              )}
            </>
          )}
          {outcomes && (
            <div className="space-y-3 rounded-medium border border-primary-200 bg-primary-50 p-3">
              <section className="space-y-1">
                <p className="text-sm font-semibold">
                  {t(
                    accepted
                      ? "pickup.detail.recordedPickup"
                      : "pickup.detail.proposedPickup"
                  )}
                </p>
                {outcomes.proposedTiming ? (
                  <>
                    <p>
                      {formatDateInTz(
                        outcomes.proposedTiming.scheduledDate,
                        outcomes.proposedTiming.timeZone,
                        "EEE, MMM d",
                        locale
                      )}
                    </p>
                    {outcomes.proposedTiming.timingAvailability ===
                      "CAPTURED" &&
                    outcomes.proposedTiming.slot &&
                    outcomes.proposedTiming.timeZone ? (
                      <p>
                        {outcomes.proposedTiming.slot.name} ·{" "}
                        {formatSlotRangeInTz(
                          outcomes.proposedTiming.slot,
                          outcomes.proposedTiming.timeZone,
                          outcomes.proposedTiming.scheduledDate,
                          locale
                        )}{" "}
                        · {outcomes.proposedTiming.timeZone}
                      </p>
                    ) : (
                      <p>{t("pickup.timingUnknown")}</p>
                    )}
                  </>
                ) : (
                  <p>{t("pickup.detail.receiptTimingUnavailable")}</p>
                )}
              </section>
              <p className="text-sm font-semibold">
                {t("pickup.detail.updatedDelivery")}
              </p>
              {outcomes.deliveryOutcomes.map((outcome) => (
                <div key={outcome.shipmentId} className="text-sm">
                  <span className="font-medium">
                    SHP-
                    {
                      outcomes.perShipment.find(
                        (shipment) => shipment.id === outcome.shipmentId
                      )?.shipmentNumber
                    }
                  </span>
                  <p>{deliveryLabel(outcome, locale)}</p>
                  {outcome.locationSummary && (
                    <p>
                      {t("pickup.detail.confirmLocations", {
                        dispatch: outcome.locationSummary.dispatch,
                        dropoff:
                          outcome.locationSummary.dropoff ??
                          t("pickup.detail.noCounter"),
                      })}
                    </p>
                  )}
                  <p className="text-xs text-default-600">{outcome.timeZone}</p>
                </div>
              ))}
              <p className="text-xs text-default-600">
                {t("pickup.detail.samePaidAmount")}
              </p>
            </div>
          )}
          {error && (
            <p
              role="alert"
              className="rounded-medium bg-danger-50 p-3 text-sm text-danger-700"
            >
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={close} isDisabled={pending}>
            {t(accepted ? "actions.done" : "actions.cancel")}
          </Button>
          {!accepted && (
            <Button
              color="primary"
              onPress={() => void (preview ? confirm() : review())}
              isLoading={pending}
              isDisabled={
                booking?.derivedState !== "Scheduled" ||
                selectedIds.length === 0 ||
                !newDate ||
                !currentSlotId ||
                newDate === booking?.scheduledDate.slice(0, 10)
              }
            >
              {t(
                preview
                  ? "pickup.detail.confirmMove"
                  : "pickup.detail.reviewMove"
              )}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
